export class FpValueService {

  /**
   * Modifies a numerical value by formatting it with a prefix, suffix and specifying decimals.
   * Large numbers are formatted with suffix (K, M and B).
   * Negative values are wrapped in parentheses.
   *
   * @param value - The numeric value to be formatted.
   * @param prefix - string value
   * @param suffix - string value
   * @param decimals - The number of decimal places to include in the formatted value.
   * @returns The formatted string representation of the value.
   *
   * @example
   * // Basic Formatting:
   * FpValueService.modify(123.456); // "123.46"
   *
   * @example
   * // Formatting with Prefix:
   * FpValueService.modify(1234.56, '$'); // "$1.23K"
   *
   * @example
   * // Formatting with Suffix:
   * FpValueService.modify(0.456, '', '%'); // "0.46%"
   *
   * @example
   * // Custom Decimal Places:
   * FpValueService.modify(12345.6789, '', '', 1); // "12.3K"
   *
   * @example
   * // Handling Negative Values:
   * FpValueService.modify(-12345.6789, '$'); // "($12.35K)"
   *
   * @example
   * // Formatting Large Numbers:
   * FpValueService.modify(12345678, '$'); // "$12.35M"
   * FpValueService.modify(1234567890, '$'); // "$1.23B"
   *
   * @example
   * // Handling Zero and Nullish Values:
   * FpValueService.modify(0); // "-"
   * FpValueService.modify(null); // "-"
   * FpValueService.modify(undefined); // "-"
   */
  static modify(value: number, prefix = '', suffix = '', decimals = 1): string {
    if (!value) {
      return '-';
    }
    const absValue = Math.abs(value);
    if (absValue < 1_000) {
      const modifiedValue = absValue.toFixed(decimals);
      if (value < 0) {
        return '(' + prefix + modifiedValue + suffix + ')';
      }
      return prefix + modifiedValue + suffix;
    }
    if (absValue < 1_000_000) {
      const modifiedValue = (absValue / 1_000).toFixed(decimals);
      if (value < 0) {
        return '(' + prefix + modifiedValue + 'K)';
      }
      return prefix + modifiedValue + 'K';
    }
    if (absValue < 1_000_000_000) {
      const modifiedValue = (absValue / 1_000_000).toFixed(decimals);
      if (value < 0) {
        return '(' + prefix + modifiedValue + 'M)';
      }
      return prefix + modifiedValue + 'M';
    }
    if (absValue < 1_000_000_000_000) {
      const modifiedValue = (absValue / 1_000_000_000).toFixed(decimals);
      if (value < 0) {
        return '(' + prefix + modifiedValue + 'B)';
      }
      return prefix + modifiedValue + 'B';
    }
  }
}
